import React, { useState, useEffect } from 'react';
import {
  Modal, Stack, Text, Paper, Badge, Box, Divider, Group, LoadingOverlay, Button
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconTicket, IconUser, IconClock, IconCheck, IconAlertCircle, IconX, IconRefresh
} from '@tabler/icons-react';

// External Captive Portal API URL
const EXTERNAL_API_URL = 'https://dev-portal.rprprime.com/v1/captive';
const CSRF_TOKEN = 'EKuyzIWuv2v50fNYvlFI6AMp5R2WujPmdlWZlUKAgH8dlEemRAr4wWGnCjQrbJ93';

/**
 * VoucherLatestModal - Modal for displaying the latest purchased voucher
 * @param {boolean} opened - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {string} customerPhone - Optional phone number to filter latest voucher
 */
export default function VoucherLatestModal({ opened, onClose, customerPhone }) {
  const [voucherData, setVoucherData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch latest voucher when modal opens
  useEffect(() => {
    if (opened) {
      fetchLatestVoucher();
    }
  }, [opened, customerPhone]);

  const fetchLatestVoucher = async () => {
    setLoading(true);
    setError(null);

    try {
      // Build query - if customerPhone is provided, filter by it
      let url = `${EXTERNAL_API_URL}/vouchers/?limit=1`;
      if (customerPhone && customerPhone.trim()) {
        url = `${EXTERNAL_API_URL}/vouchers/?search=${encodeURIComponent(customerPhone)}&limit=1`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'X-CSRFTOKEN': CSRF_TOKEN
        }
      });

      const data = await response.json();
      setLoading(false);

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch latest voucher');
      }

      const vouchers = Array.isArray(data) ? data : data.results || [];
      
      if (vouchers.length > 0) {
        // Get the most recent one (first in the list is usually the latest)
        const voucher = vouchers[0];
        
        const transformedData = {
          voucherCode: voucher.code,
          custName: voucher.cust_name,
          custNumber: voucher.cust_number,
          projectId: voucher.project_id,
          price: voucher.price,
          period: voucher.period,
          dataLimitMB: voucher.data_limit_mb,
          status: voucher.status,
          isFree: voucher.is_free,
          activatedAt: voucher.activated_at,
          expiryTime: voucher.expiry_time,
          createdAt: voucher.created_at,
          isExpired: voucher.status === 'expired' || (voucher.expiry_time && new Date(voucher.expiry_time) < new Date()),
          timeRemainingMs: voucher.expiry_time ? Math.max(0, new Date(voucher.expiry_time) - new Date()) : 0,
          timeRemainingMinutes: voucher.expiry_time ? Math.round((new Date(voucher.expiry_time) - new Date()) / 60000) : 0,
          timeRemainingFormatted: voucher.expiry_time ? (() => {
            const mins = Math.max(0, Math.round((new Date(voucher.expiry_time) - new Date()) / 60000));
            return `${Math.floor(mins / 60)}h ${mins % 60}m`;
          })() : 'N/A'
        };
        
        setVoucherData(transformedData);
        notifications.show({
          title: 'Latest Voucher Found',
          message: `Voucher ${voucher.code} retrieved`,
          color: 'violet',
          autoClose: 3000,
          icon: <IconCheck size={16} />
        });
      } else {
        throw new Error('No voucher found');
      }
    } catch (err) {
      setLoading(false);
      setError(err.message);
      notifications.show({
        title: 'No Voucher Found',
        message: err.message,
        color: 'orange',
        autoClose: 3000
      });
    }
  };

  const handleClose = () => {
    setVoucherData(null);
    setError(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconTicket size={20} color="#9381ff" />
          <Text fw={600} c="#2d2d2d">Latest Purchased Voucher</Text>
        </Group>
      }
      size="md"
      centered
      closeButtonProps={{ icon: <IconX size={18} /> }}
    >
      <LoadingOverlay visible={loading} overlayProps={{ blur: 2 }} />
      
      <Stack gap="md">
        {/* Refresh Button */}
        <Button 
          variant="light" 
          color="violet" 
          size="sm"
          leftSection={<IconRefresh size={16} />}
          onClick={fetchLatestVoucher}
          loading={loading}
        >
          Refresh
        </Button>

        <Divider />

        {/* Error Message */}
        {error && !voucherData && (
          <Stack align="center" gap="xs" py="md">
            <IconAlertCircle size={32} color="#fa5252" />
            <Text c="red" size="sm" ta="center">
              {error}
            </Text>
          </Stack>
        )}

        {/* Voucher Data Display */}
        {!loading && voucherData && (
          <Stack gap="md">
            {/* Voucher Code */}
            <Box>
              <Group gap="xs" mb={4}>
                <IconTicket size={16} color="#9381ff" />
                <Text size="xs" c="dimmed">Voucher Code</Text>
              </Group>
              <Badge 
                size="xl" 
                variant="light" 
                color="violet"
                style={{ textTransform: 'uppercase' }}
              >
                {voucherData.voucherCode || 'N/A'}
              </Badge>
            </Box>

            <Divider color="#e8e5f0" />

            {/* Customer Name */}
            <Box>
              <Group gap="xs" mb={4}>
                <IconUser size={16} color="#228be6" />
                <Text size="xs" c="dimmed">Customer Name</Text>
              </Group>
              <Text c="#2d2d2d" fw={600} size="sm">
                {voucherData.custName || 'N/A'}
              </Text>
            </Box>

            <Divider color="#e8e5f0" />

            {/* Phone Number */}
            <Box>
              <Text size="xs" c="dimmed">Phone Number</Text>
              <Text c="#2d2d2d" fw={500} size="sm">
                {voucherData.custNumber || 'N/A'}
              </Text>
            </Box>

            <Divider color="#e8e5f0" />

            {/* Duration */}
            <Box>
              <Group gap="xs" mb={4}>
                <IconClock size={16} color="#be4bdb" />
                <Text size="xs" c="dimmed">Duration</Text>
              </Group>
              <Text c="#2d2d2d" fw={600} size="lg">
                {voucherData.period || 0} minutes
              </Text>
            </Box>

            <Divider color="#e8e5f0" />

            {/* Expiry Time */}
            <Box>
              <Group gap="xs" mb={4}>
                <IconClock size={16} color={voucherData.isExpired ? '#fa5252' : '#be4bdb'} />
                <Text size="xs" c="dimmed">Expiry Time</Text>
              </Group>
              <Text 
                c={voucherData.isExpired ? 'red' : '#2d2d2d'}
                fw={600} 
                size="sm"
              >
                {voucherData.expiryTime ? new Date(voucherData.expiryTime).toLocaleString() : 'N/A'}
              </Text>
              <Text c={voucherData.isExpired ? 'red' : 'dimmed'} size="xs" mt={4}>
                {voucherData.timeRemainingFormatted || 'Expired'}
              </Text>
            </Box>

            <Divider color="#e8e5f0" />

            {/* Status Badge */}
            <Box>
              <Group justify="center">
                <Badge 
                  size="lg" 
                  variant="dot"
                  color={voucherData.status === 'active' ? 'green' : voucherData.status === 'expired' ? 'red' : 'gray'}
                >
                  {voucherData.status?.toUpperCase() || 'UNKNOWN'}
                </Badge>
              </Group>
            </Box>

            {/* Additional Info */}
            <Box>
              <Divider color="#e8e5f0" mb="md" />
              <Group gap="lg">
                <Box>
                  <Text size="xs" c="dimmed">Project ID</Text>
                  <Text size="sm" fw={500}>{voucherData.projectId || 'N/A'}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">Price</Text>
                  <Text size="sm" fw={500}>{voucherData.price || '0.00'}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">Free</Text>
                  <Text size="sm" fw={500}>{voucherData.isFree ? 'Yes' : 'No'}</Text>
                </Box>
              </Group>
              {voucherData.createdAt && (
                <Text size="xs" c="dimmed" mt="sm">
                  Created: {new Date(voucherData.createdAt).toLocaleString()}
                </Text>
              )}
            </Box>
          </Stack>
        )}

        {/* Empty State */}
        {!loading && !voucherData && !error && (
          <Text c="dimmed" size="sm" ta="center" py="md">
            No voucher data available. Click Refresh to load the latest voucher.
          </Text>
        )}
      </Stack>
    </Modal>
  );
}